import { Check, Copy } from 'lucide-react';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  canShareClientCredentials,
  copyClientCredentials,
  formatClientCredentials,
} from '../lib/client-credentials';
import ShareButton from './ShareButton';

interface ClientCredentialsBlockProps {
  login: string;
  password: string;
  compact?: boolean;
}

const ClientCredentialsBlock = ({
  login,
  password,
  compact = false,
}: ClientCredentialsBlockProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const canShare = canShareClientCredentials();

  const getText = () => formatClientCredentials(login, password);

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    await copyClientCredentials(login, password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (compact) {
    return (
      <div className="mt-3 space-y-1.5 text-sm text-slate-600 min-w-0">
        <p className="truncate">
          <span className="text-slate-400">{t('Логин')}:</span> {login}
        </p>
        <div className="flex items-center gap-2 min-w-0">
          <p className="truncate flex-1">
            <span className="text-slate-400">{t('Пароль')}:</span>{' '}
            <span className="font-mono">{password}</span>
          </p>
          <div className="flex shrink-0 items-center gap-0.5">
            {canShare && (
              <ShareButton
                getText={getText}
                title="ProТренерофф"
                ariaLabel={t('Поделиться данными для входа')}
                className="btn-ghost p-1.5"
                iconSize={14}
              />
            )}
            <button
              type="button"
              className="btn-ghost p-1.5"
              onClick={(e) => void handleCopy(e)}
              aria-label={t('Копировать данные для входа')}
            >
              {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-semibold text-slate-800">{t('Данные для входа в приложение')}</p>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <span className="text-slate-500">{t('Логин')}</span>
          <span className="font-medium text-slate-900 font-mono">{login}</span>
        </div>
        <div className="flex justify-between gap-3">
          <span className="text-slate-500">{t('Пароль')}</span>
          <span className="font-medium text-slate-900 font-mono">{password}</span>
        </div>
      </div>
      <div className={`grid gap-2 ${canShare ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
        {canShare && (
          <ShareButton
            getText={getText}
            title="ProТренерофф"
            ariaLabel={t('Поделиться данными для входа')}
            variant="primary"
          />
        )}
        <button type="button" className="btn-secondary w-full" onClick={(e) => void handleCopy(e)}>
          {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
          <span>{copied ? t('Скопировано') : t('Копировать')}</span>
        </button>
      </div>
    </div>
  );
};

export default ClientCredentialsBlock;
